import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  IconButton,
  Checkbox,
  Paper,
  CircularProgress,
  alpha,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

interface ClinicInfo {
  clinicName: string;
  phone: string;
  email: string;
  address: string;
  lineId: string;
  logoUrl: string;
}

interface ThemeSettings {
  primaryColor: string;
  secondaryColor: string;
  template: "clean" | "premium";
  selectedTheme: "beauty-clinic" | "medical-center" | "modern-clinic";
}

interface PageSelection {
  home: boolean;
  services: boolean;
  doctors: boolean;
  about: boolean;
  contact: boolean;
  blog: boolean;
}

interface OnboardingWizardProps {
  onComplete: (data: { clinicInfo: ClinicInfo; theme: ThemeSettings; pages: PageSelection }) => void;
  onCancel: () => void;
}

const THEMES = [
  {
    id: "beauty-clinic" as const,
    name: "Beauty Clinic",
    emoji: "💆‍♀️",
    label: "Beauty",
    description: "Elegant, soft aesthetic for beauty & spa clinics",
    primaryColor: "#D4A574",
    secondaryColor: "#F8E8DC",
    accentColor: "#ec4899",
    gradientFrom: "#fce7f3",
    gradientTo: "#fecdd3",
    borderColor: "#f472b6",
    bgColor: "#fdf2f8",
  },
  {
    id: "medical-center" as const,
    name: "Medical Center",
    emoji: "🏥",
    label: "Medical",
    description: "Professional, trustworthy for hospitals & clinics",
    primaryColor: "#0369a1",
    secondaryColor: "#0f172a",
    accentColor: "#ef4444",
    gradientFrom: "#dbeafe",
    gradientTo: "#a5f3fc",
    borderColor: "#60a5fa",
    bgColor: "#eff6ff",
  },
  {
    id: "modern-clinic" as const,
    name: "Modern Clinic",
    emoji: "✨",
    label: "Premium",
    description: "Luxury aesthetic for high-end clinics",
    primaryColor: "#3b82f6",
    secondaryColor: "#8b5cf6",
    accentColor: "#1e293b",
    gradientFrom: "#60a5fa",
    gradientTo: "#a78bfa",
    borderColor: "#a78bfa",
    bgColor: "#f5f3ff",
  },
];

const PAGES = [
  { key: "home", label: "Home", desc: "Main landing page with hero, services overview", icon: "🏠", required: true },
  { key: "services", label: "Services", desc: "List of treatments and procedures", icon: "💉", required: false },
  { key: "doctors", label: "Doctors", desc: "Team member profiles", icon: "👨‍⚕️", required: false },
  { key: "about", label: "About", desc: "Clinic story and mission", icon: "📖", required: false },
  { key: "contact", label: "Contact", desc: "Contact form, map, and info", icon: "📞", required: false },
  { key: "blog", label: "Blog", desc: "Health articles and news", icon: "📰", required: false },
];

export const OnboardingWizard = ({ onComplete, onCancel }: OnboardingWizardProps) => {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);

  const [clinicInfo, setClinicInfo] = useState<ClinicInfo>({
    clinicName: "",
    phone: "",
    email: "",
    address: "",
    lineId: "",
    logoUrl: "",
  });

  const [theme, setTheme] = useState<ThemeSettings>({
    primaryColor: "#3B82F6",
    secondaryColor: "#10B981",
    template: "clean",
    selectedTheme: "beauty-clinic",
  });

  const [pages, setPages] = useState<PageSelection>({
    home: true,
    services: true,
    doctors: true,
    about: false,
    contact: true,
    blog: false,
  });

  const totalSteps = 4;

  // Phone number validation - only allow numbers
  const handlePhoneChange = (value: string) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    setClinicInfo({ ...clinicInfo, phone: cleaned });
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    onComplete({ clinicInfo, theme, pages });
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return clinicInfo.clinicName.trim() !== "";
      case 2:
        return true;
      case 3:
        return Object.values(pages).some((v) => v);
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleThemeSelect = (themeId: "beauty-clinic" | "medical-center" | "modern-clinic") => {
    const selectedTheme = THEMES.find((t) => t.id === themeId);
    if (selectedTheme) {
      setTheme({
        ...theme,
        selectedTheme: themeId,
        template: themeId === "modern-clinic" ? "premium" : "clean",
        primaryColor: selectedTheme.primaryColor,
        secondaryColor: selectedTheme.secondaryColor,
      });
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "white",
      "& fieldset": { borderColor: "grey.300" },
      "&:hover fieldset": { borderColor: "grey.400" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
    },
    "& .MuiInputBase-input": { color: "grey.900" },
    "& .MuiInputLabel-root": { color: "grey.600" },
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          maxHeight: "90vh",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5" fontWeight={700}>
            Create Your Clinic Website
          </Typography>
          <IconButton onClick={onCancel} sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Progress Steps */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {[1, 2, 3, 4].map((s) => (
            <Box key={s} sx={{ flex: 1 }}>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: s <= step ? "white" : "rgba(255,255,255,0.3)",
                  transition: "background-color 0.3s",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  display: "block",
                  color: s <= step ? "white" : "rgba(255,255,255,0.5)",
                }}
              >
                {s === 1 && "Info"}
                {s === 2 && "Style"}
                {s === 3 && "Pages"}
                {s === 4 && "Preview"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, bgcolor: "white", maxHeight: "calc(90vh - 200px)", overflow: "auto" }}>
        {/* Step 1: Clinic Information */}
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="h6" fontWeight={600} color="grey.900">
              Clinic Information
            </Typography>

            <Box>
              <Typography variant="body2" color="grey.700" sx={{ mb: 1 }}>
                Clinic Name{" "}
                <Typography component="span" color="error.main">
                  *
                </Typography>
              </Typography>
              <TextField
                fullWidth
                value={clinicInfo.clinicName}
                onChange={(e) => setClinicInfo({ ...clinicInfo, clinicName: e.target.value })}
                placeholder="e.g., Dr. Smith Clinic"
                sx={textFieldSx}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="body2" color="grey.700" sx={{ mb: 1 }}>
                  Phone
                </Typography>
                <TextField
                  fullWidth
                  value={clinicInfo.phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="0812345678"
                  inputProps={{ inputMode: "tel" }}
                  helperText="ตัวเลขเท่านั้น"
                  sx={textFieldSx}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="grey.700" sx={{ mb: 1 }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  value={clinicInfo.email}
                  onChange={(e) => setClinicInfo({ ...clinicInfo, email: e.target.value })}
                  placeholder="contact@clinic.com"
                  sx={textFieldSx}
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" color="grey.700" sx={{ mb: 1 }}>
                Address
              </Typography>
              <TextField
                fullWidth
                value={clinicInfo.address}
                onChange={(e) => setClinicInfo({ ...clinicInfo, address: e.target.value })}
                placeholder="123 Medical Street, Bangkok"
                sx={textFieldSx}
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="body2" color="grey.700" sx={{ mb: 1 }}>
                  LINE ID
                </Typography>
                <TextField
                  fullWidth
                  value={clinicInfo.lineId}
                  onChange={(e) => setClinicInfo({ ...clinicInfo, lineId: e.target.value })}
                  placeholder="@clinicname"
                  sx={textFieldSx}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="grey.700" sx={{ mb: 1 }}>
                  Logo URL
                </Typography>
                <TextField
                  fullWidth
                  value={clinicInfo.logoUrl}
                  onChange={(e) => setClinicInfo({ ...clinicInfo, logoUrl: e.target.value })}
                  placeholder="https://..."
                  sx={textFieldSx}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Step 2: Theme Selection */}
        {step === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="h6" fontWeight={600} color="grey.900">
              Choose Your Theme
            </Typography>
            <Typography variant="body2" color="grey.600">
              Select a theme that matches your clinic type
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              {THEMES.map((t) => (
                <Paper
                  key={t.id}
                  onClick={() => handleThemeSelect(t.id)}
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    border: 2,
                    borderColor: theme.selectedTheme === t.id ? t.borderColor : "transparent",
                    bgcolor: theme.selectedTheme === t.id ? t.bgColor : "grey.50",
                    borderRadius: 3,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: t.borderColor,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 100,
                      borderRadius: 2,
                      mb: 2,
                      background: `linear-gradient(135deg, ${t.gradientFrom} 0%, ${t.gradientTo} 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Typography sx={{ fontSize: "2rem" }}>{t.emoji}</Typography>
                    <Box
                      sx={{
                        mt: 1,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: t.id === "modern-clinic" ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.8)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={t.id === "modern-clinic" ? "white" : t.borderColor}
                      >
                        {t.label}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography fontWeight={600} color="grey.900">
                    {t.name}
                  </Typography>
                  <Typography variant="caption" color="grey.600">
                    {t.description}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: t.primaryColor }} />
                    <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: t.secondaryColor }} />
                    <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: t.accentColor }} />
                  </Box>
                </Paper>
              ))}
            </Box>

            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`,
              }}
            >
              <Typography color="white" fontWeight={600}>
                Preview: {clinicInfo.clinicName || "Your Clinic"}
              </Typography>
              <Typography color="rgba(255,255,255,0.8)" variant="body2">
                Theme: {THEMES.find((t) => t.id === theme.selectedTheme)?.name}
              </Typography>
            </Box>

            <Paper sx={{ p: 2, bgcolor: "#eff6ff", border: 1, borderColor: "#bfdbfe", borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} color="#1e40af" gutterBottom>
                What's included:
              </Typography>
              <Typography variant="body2" color="#1e40af">
                Each theme comes with a complete set of matching pages (Home, About, Services, Doctors, Contact) with
                consistent styling and your clinic information pre-filled.
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Step 3: Page Selection */}
        {step === 3 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" fontWeight={600} color="grey.900">
              Select Pages to Create
            </Typography>
            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
              Choose which pages you want on your website. You can add more later.
            </Typography>

            {PAGES.map((page) => (
              <Paper
                key={page.key}
                onClick={() =>
                  !page.required && setPages({ ...pages, [page.key]: !pages[page.key as keyof PageSelection] })
                }
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  cursor: page.required ? "default" : "pointer",
                  border: 2,
                  borderColor: pages[page.key as keyof PageSelection] ? "primary.main" : "grey.200",
                  bgcolor: pages[page.key as keyof PageSelection] ? alpha("#3b82f6", 0.05) : "white",
                  borderRadius: 2,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: page.required ? undefined : "grey.300",
                  },
                }}
              >
                <Checkbox
                  checked={pages[page.key as keyof PageSelection]}
                  onChange={(e) => !page.required && setPages({ ...pages, [page.key]: e.target.checked })}
                  disabled={page.required}
                />
                <Typography sx={{ fontSize: "1.5rem" }}>{page.icon}</Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={500} color="grey.900">
                    {page.label}
                    {page.required && (
                      <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 1 }}>
                        (Required)
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="grey.500">
                    {page.desc}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* Step 4: Preview & Confirm */}
        {step === 4 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="h6" fontWeight={600} color="grey.900">
              Ready to Create!
            </Typography>

            <Paper sx={{ p: 3, bgcolor: "grey.50", borderRadius: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography fontWeight={600} color="grey.700" gutterBottom>
                  Clinic Information
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "white", borderRadius: 2 }}>
                  <Typography variant="body2" color="grey.900">
                    <Typography component="span" color="grey.500">
                      Name:
                    </Typography>{" "}
                    {clinicInfo.clinicName}
                  </Typography>
                  {clinicInfo.phone && (
                    <Typography variant="body2" color="grey.900">
                      <Typography component="span" color="grey.500">
                        Phone:
                      </Typography>{" "}
                      {clinicInfo.phone}
                    </Typography>
                  )}
                  {clinicInfo.email && (
                    <Typography variant="body2" color="grey.900">
                      <Typography component="span" color="grey.500">
                        Email:
                      </Typography>{" "}
                      {clinicInfo.email}
                    </Typography>
                  )}
                  {clinicInfo.address && (
                    <Typography variant="body2" color="grey.900">
                      <Typography component="span" color="grey.500">
                        Address:
                      </Typography>{" "}
                      {clinicInfo.address}
                    </Typography>
                  )}
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography fontWeight={600} color="grey.700" gutterBottom>
                  Theme
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "white", borderRadius: 2, display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`,
                    }}
                  />
                  <Box>
                    <Typography fontWeight={500} color="grey.900">
                      {THEMES.find((t) => t.id === theme.selectedTheme)?.name}
                    </Typography>
                    <Typography variant="body2" color="grey.500">
                      {theme.primaryColor} / {theme.secondaryColor}
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              <Box>
                <Typography fontWeight={600} color="grey.700" gutterBottom>
                  Pages to Create
                </Typography>
                <Paper sx={{ p: 2, bgcolor: "white", borderRadius: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Object.entries(pages)
                    .filter(([, enabled]) => enabled)
                    .map(([key]) => (
                      <Box
                        key={key}
                        sx={{
                          px: 2,
                          py: 0.5,
                          bgcolor: alpha("#3b82f6", 0.1),
                          color: "#1d4ed8",
                          borderRadius: 4,
                          textTransform: "capitalize",
                        }}
                      >
                        <Typography variant="body2">{key}</Typography>
                      </Box>
                    ))}
                </Paper>
              </Box>
            </Paper>

            <Paper sx={{ p: 2, bgcolor: "#ecfdf5", border: 1, borderColor: "#a7f3d0", borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} color="#065f46" gutterBottom>
                What happens next:
              </Typography>
              <Typography variant="body2" color="#065f46" component="ul" sx={{ pl: 2, m: 0 }}>
                <li>Your pages will be created with the selected template</li>
                <li>Content will be pre-filled with your clinic info</li>
                <li>You can edit everything in the page builder</li>
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          borderTop: 1,
          borderColor: "grey.200",
          bgcolor: "grey.50",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button onClick={step === 1 ? onCancel : handleBack} sx={{ color: "grey.600" }}>
          {step === 1 ? "Cancel" : "Back"}
        </Button>

        {step < totalSteps ? (
          <Button variant="contained" onClick={handleNext} disabled={!canProceed()}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isGenerating}
            color="success"
            startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isGenerating ? "Creating..." : "Create Website"}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};
